import { FC, useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { coreService } from 'network-utils';
import { SearchAutocomplete } from 'learning-panel-components';
import { selectToken } from '@redux/slices/userSlice';
import { useGetSearchContent } from '@queries/core/search';
import { mixpanelTrack, gainsightTrack } from 'utils';

const SearchContainer: FC<SearchProps> = ({ setVideoTutorialUuid }: SearchProps) => {
    const [allContent, setAllContent] = useState<coreService.SearchContent[]>([]);
    const [searchValue, setSearchValue] = useState<string | undefined>(undefined);
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const [isAssignedToMe, setIsAssignedToMe] = useState(false);
    const [searchOptions, setSearchOptions] = useState<coreService.SearchContent[]>([]);
    const [languages, setLanguages] = useState<string[]>([]);
    const [contentTypes, setContentTypes] = useState<string[]>([]);
    const [progresses, setProgresses] = useState<string[]>([]);
    const [filterState, setFilterState] = useState<FilterState>({
        Language: [],
        Type: [],
        Progress: [],
        Assigned: 'Off'
    });

    const token = useSelector(selectToken);
    const { data } = useGetSearchContent(token);
    const { data: serverData } = data || {};

    const searchContentData = useMemo(() => {
        if (!serverData) {
            return [];
        }

        const progressDisplayNameMap: { [key: string]: string } = {
            not_started: 'Not Started',
            started: 'Started',
            completed: 'Completed'
        };

        return serverData?.map(option => {
            const status = progressDisplayNameMap[option.status];
            const onClickHandler = option.contentType === 'Video' ? () => setVideoTutorialUuid(option.url) : undefined;
            const updatedOption = {
                ...option,
                status,
                onClick: onClickHandler
            };

            return updatedOption;
        });
    }, [serverData, setVideoTutorialUuid]);

    useEffect(() => {
        if (searchContentData?.length && searchContentData.length > 0) {
            setAllContent(searchContentData);
            setAvailableFilters(searchContentData);
        }
    }, [searchContentData]);

    useEffect(() => {
        let tmpRecords = [...allContent];
        const mixPanelPayload = { ...filterState };

        if (searchValue !== undefined) {
            tmpRecords = tmpRecords.filter(
                record =>
                    record?.displayName?.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase()) ||
                    record?.description?.toLocaleLowerCase().includes(searchValue.toLocaleLowerCase())
            );
            if (selectedFilters.length !== 0) {
                if (languages.some(selectedFilter => selectedFilters.includes(selectedFilter))) {
                    tmpRecords = tmpRecords.filter(record => selectedFilters.includes(record?.language));
                    const uniqueLanguages = tmpRecords
                        .map(record => record.language)
                        .filter((value, index, self) => self.indexOf(value) === index);
                    mixPanelPayload.Language = [...uniqueLanguages];
                }
                if (contentTypes.some(selectedFilter => selectedFilters.includes(selectedFilter))) {
                    tmpRecords = tmpRecords.filter(record => selectedFilters.includes(record?.contentType));
                    const uniqueTypes = tmpRecords
                        .map(record => record.contentType)
                        .filter((value, index, self) => self.indexOf(value) === index);
                    mixPanelPayload.Type = [...uniqueTypes];
                }
                if (progresses.some(selectedFilter => selectedFilters.includes(selectedFilter))) {
                    tmpRecords = tmpRecords.filter(record => selectedFilters.includes(record?.status));
                    const uniqueProgresses = tmpRecords
                        .map(record => record.status)
                        .filter((value, index, self) => self.indexOf(value) === index);
                    mixPanelPayload.Progress = [...uniqueProgresses];
                }
            }
            if (isAssignedToMe) {
                tmpRecords = tmpRecords.filter(record => record?.assignedToMe);
                mixPanelPayload.Assigned = tmpRecords[0]?.assignedToMe ? 'On' : 'Off';
            }

            setFilterState(mixPanelPayload);
            triggerMixpanel('Search Item Triggered', mixPanelPayload);
            setSearchOptions(tmpRecords);
        }
    }, [searchValue, isAssignedToMe, selectedFilters]);

    const setAvailableFilters = (options: coreService.SearchContent[]) => {
        const availableLanguages: string[] = [];
        const availableTypes: string[] = [];
        const availableProgresses: string[] = [];
        options.forEach(option => {
            !availableLanguages.includes(option?.language) &&
                option?.language &&
                availableLanguages.push(option?.language);
            !availableTypes.includes(option?.contentType) &&
                option?.contentType &&
                availableTypes.push(option?.contentType);
            !availableProgresses.includes(option?.status) && option?.status && availableProgresses.push(option?.status);
        });
        setLanguages(availableLanguages);
        setContentTypes(availableTypes);
        setProgresses(availableProgresses);
    };

    const triggerMixpanel = (eventName: string, properties: {}) => {
        mixpanelTrack({
            eventName,
            properties
        });
        gainsightTrack(eventName, properties);
    };

    return (
        <SearchAutocomplete
            triggerMixpanel={triggerMixpanel}
            languages={languages}
            types={contentTypes}
            progress={progresses}
            options={searchOptions}
            assignedToMe={isAssignedToMe}
            selectedFilters={selectedFilters}
            setAssignedToMe={setIsAssignedToMe}
            setSelectedFilters={setSelectedFilters}
            searchString={searchValue}
            setSearchString={(value: string) => setSearchValue(value)}
        />
    );
};

interface SearchProps {
    setVideoTutorialUuid: Function;
}

interface FilterState {
    Language: string[];
    Type: string[];
    Progress: string[];
    Assigned: string;
}

export default SearchContainer;
